/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/button-has-type */
import classNames from 'classnames';
import React, { ReactNode, MouseEventHandler } from 'react';
import { Link } from 'wouter';

import styles from '../../../styles/buttons/button.module.css';

export interface ButtonProps {
    type?: 'button' | 'submit' | 'reset';
    href?: string | null;
    external?: boolean;
    direct?: boolean;
    target?: string;
    label?: ReactNode;
    children?: ReactNode;
    icon?: ReactNode;
    iconPosition?: 'left' | 'right' | 'inline';
    disabled?: boolean;
    loading?: boolean;
    disableOnLoading?: boolean;
    className?: string | null;
    iconClassName?: string | null;
    labelClassName?: string | null;
    onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> | null;
    buttonRef?: React.Ref<HTMLButtonElement | HTMLAnchorElement> | null;
}

function Button({
    type = 'button',
    href = null,
    external = false,
    direct = false,
    target = '_blank',
    label = null,
    children = null,
    icon = null,
    iconPosition = 'inline',
    disabled = false,
    loading = false,
    disableOnLoading = true,
    onClick = null,
    className = null,
    iconClassName = null,
    labelClassName = null,
    buttonRef = null,
}: ButtonProps) {
    const finalLabel = label || children;
    const hasChildren = label !== null && children !== null;
    const hasIcon = icon !== null;
    const hasInlineIcon = hasIcon && iconPosition === 'inline';
    const hasIconColumns = hasIcon && !hasInlineIcon;
    const content = (
        <>
            {hasInlineIcon ? (
                <>
                    <span className={classNames([styles.icon, iconClassName])}>{icon}</span>
                    {finalLabel !== null ? (
                        <span className={classNames([styles.label, labelClassName])}>
                            {finalLabel}
                        </span>
                    ) : null}
                </>
            ) : null}
            {hasIconColumns ? (
                <>
                    <span className={classNames([styles.left])}>
                        {iconPosition === 'left' ? icon : null}
                    </span>
                    <span className={classNames([styles.center])}>{finalLabel}</span>
                    <span className={classNames([styles.right])}>
                        {iconPosition === 'right' ? icon : null}
                    </span>
                    {hasChildren ? children : null}
                </>
            ) : null}
            {!hasIcon ? finalLabel : null}
            {hasChildren ? children : null}
        </>
    );

    const buttonClassNames = classNames([
        styles.container,
        {
            [styles.withIcon]: hasIcon,
            [styles.withIconColumns]: hasIconColumns,
            [styles.withText]: finalLabel !== null,
            [styles.isLink]: href !== null,
            [styles.isDisabled]: disabled,
            [styles.isLoading]: loading,
        },
        className,
    ]);
    if (href !== null) {
        return external || direct ? (
            <a
                href={href}
                className={buttonClassNames}
                onClick={onClick!}
                target={external ? target : undefined}
                ref={buttonRef as React.Ref<HTMLAnchorElement>}
            >
                {content}
            </a>
        ) : (
            <Link
                href={href}
                onClick={onClick!}
                className={buttonClassNames}
                ref={buttonRef as React.Ref<HTMLAnchorElement>}
            >
                {content}
            </Link>
        );
    }
    return (
        <button
            type={type}
            className={buttonClassNames}
            onClick={onClick!}
            disabled={disabled || (disableOnLoading && loading)}
            ref={buttonRef as React.Ref<HTMLButtonElement>}
        >
            {content}
        </button>
    );
}

// eslint-disable-next-line react/display-name
export default React.forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    Omit<ButtonProps, 'buttonRef'>
>((props, ref) => <Button buttonRef={ref} {...props} />);
